import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Check, X } from "lucide-react";
import { formatDate } from "@/lib/format";

const LOCKED_SECTION_LABELS = {
  medical: "Medical",
  school: "School",
  emergency: "Emergency contact",
};

/**
 * Phase 3 Delivery 2 — primary-parent proposal review dialog.
 * Lists every pending proposal across all children. Approve applies the
 * change (logged to history); decline marks the proposal rejected.
 *
 * Extracted from Children.jsx (v76 refactor) — pure presentational
 * component. All state lives in the parent.
 */
export function ChildProposalsReviewDialog({
  open,
  onOpenChange,
  pendingProposals,
  proposalDecisionBusy,
  decideProposal,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto"
        aria-describedby="child-proposals-desc"
        data-testid="child-proposals-review-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Review suggestions
          </DialogTitle>
          <p id="child-proposals-desc" className="text-xs text-[#6F828A] mt-1">
            Co-parent suggestions for locked sections. Approving applies the change and records it in the child&apos;s history. Declining keeps the current value.
          </p>
        </DialogHeader>
        {pendingProposals.length === 0 ? (
          <div className="text-sm text-[#6F828A] py-8 text-center italic" data-testid="child-proposals-empty">
            No pending suggestions.
          </div>
        ) : (
          <ol className="space-y-3" data-testid="child-proposals-list">
            {pendingProposals.map((p) => (
              <li
                key={p.id}
                className="border border-[#D9E1E3] rounded-sm p-3 bg-white"
                data-testid={`child-proposal-${p.id}`}
              >
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2E3A3F]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.child_color || "#1F9FA4" }} />
                    {p.child_name}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A] bg-[#EEF3F4] border border-[#D9E1E3] rounded-sm px-1.5 py-0.5">
                    {LOCKED_SECTION_LABELS[p.section] || p.section}
                  </span>
                  <span className="text-sm font-medium ml-auto">{p.label || p.field}</span>
                </div>
                <div className="mt-2 text-sm flex items-center gap-2 flex-wrap">
                  <span className="px-2 py-1 rounded-sm bg-[#FAFBFB] border border-[#D9E1E3] text-[#825A41]">
                    {p.old_value || "—"}
                  </span>
                  <span className="text-[#6F828A]">→</span>
                  <span className="px-2 py-1 rounded-sm bg-[#E0F2F2] border border-[#A6D7D9] text-[#116B6E]">
                    {p.new_value || "—"}
                  </span>
                </div>
                {p.note && (
                  <p className="mt-2 text-[12px] text-[#6F828A] italic">“{p.note}”</p>
                )}
                <div className="mt-2 text-[11px] text-[#6F828A]">
                  Suggested by <span className="text-[#2E3A3F] font-medium">{p.proposed_by_name || "Co-parent"}</span> · {formatDate(p.proposed_at)}
                </div>
                <div className="mt-3 flex items-center justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => decideProposal(p, "reject")}
                    disabled={proposalDecisionBusy}
                    className="border-[#D9E1E3] rounded-sm h-8 text-xs"
                    data-testid={`child-proposal-${p.id}-reject`}
                  >
                    <X className="w-3 h-3 mr-1" /> Decline
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => decideProposal(p, "approve")}
                    disabled={proposalDecisionBusy}
                    className="bg-[#1F9FA4] hover:bg-[#178085] h-8 text-xs"
                    data-testid={`child-proposal-${p.id}-approve`}
                  >
                    <Check className="w-3 h-3 mr-1" /> Approve
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="child-proposals-close-button">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
